import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  CreditCard, Download, Share2, QrCode, Shield, Award,
  Loader2, ExternalLink, Copy, Check, Printer
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function DigitalCredentialPage() {
  const [credential, setCredential] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  
  const token = localStorage.getItem('upim_token');

  useEffect(() => {
    loadCredential();
  }, []);

  const loadCredential = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/credential/my-credential`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCredential(res.data);
    } catch (err) {
      console.error('Error loading credential:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyVerificationUrl = () => {
    if (credential?.verification_url) {
      navigator.clipboard.writeText(credential.verification_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const openPrintableVersion = () => {
    window.open(`${API_URL}/api/credential/my-credential/html`, '_blank');
  };

  const shareCredential = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Mi Credencial UPIM',
          text: `Verifica mi credencial de profesional inmobiliario UPIM`,
          url: credential?.verification_url
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      copyVerificationUrl();
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </>
    );
  }

  const cred = credential?.credential;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center justify-center gap-3">
              <CreditCard className="w-8 h-8 text-indigo-600" />
              Mi Credencial Digital
            </h1>
            <p className="text-gray-500 mt-2">Tu identificación verificable como profesional UPIM</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Credential Card Preview */}
            <div>
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 text-center">
                  <h2 className="text-2xl font-bold text-white tracking-wider">UPIM</h2>
                  <p className="text-indigo-200 text-xs tracking-wide">
                    UNIÓN DE PROFESIONALES INMOBILIARIOS DE MÉXICO
                  </p>
                </div>
                
                {/* Body */}
                <div className="p-8 text-center">
                  {/* Photo */}
                  <div className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-indigo-500 overflow-hidden bg-slate-700 flex items-center justify-center">
                    <span className="text-4xl font-bold text-gray-400">
                      {cred?.name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white">{cred?.name}</h3>
                  
                  <div className="inline-block mt-3 px-4 py-1.5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full">
                    <span className="text-white text-sm font-semibold tracking-wide">
                      {cred?.membership_type?.toUpperCase() || 'AFILIADO'}
                    </span>
                  </div>
                  
                  <p className="text-gray-400 text-sm mt-3">
                    Miembro desde {cred?.member_since ? new Date(cred.member_since).getFullYear() : '2025'}
                  </p>
                  
                  {/* QR Code */}
                  {credential?.qr_code && (
                    <div className="mt-6 inline-block p-3 bg-white rounded-xl">
                      <img 
                        src={credential.qr_code} 
                        alt="QR Verificación" 
                        className="w-28 h-28"
                      />
                    </div>
                  )}
                  
                  <p className="text-gray-500 text-xs mt-3">
                    Escanea para verificar
                  </p>
                  
                  {cred?.is_verified && (
                    <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 rounded-full text-white text-sm">
                      <Shield className="w-4 h-4" />
                      VERIFICADO
                    </div>
                  )}
                </div>
                
                {/* Footer */}
                <div className="bg-black/30 px-6 py-4 text-center">
                  <p className="text-gray-400 text-xs leading-relaxed">
                    Esta credencial certifica la membresía activa<br />
                    como profesional inmobiliario afiliado a UPIM
                  </p>
                </div>
              </div>
            </div>

            {/* Actions & Info */}
            <div className="space-y-6">
              {/* Actions */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4">Acciones</h3>
                <div className="space-y-3">
                  <button
                    onClick={openPrintableVersion}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    <Printer className="w-5 h-5" />
                    Ver versión imprimible
                  </button>
                  
                  <button
                    onClick={shareCredential}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-white border-2 border-indigo-600 text-indigo-600 rounded-lg hover:bg-indigo-50"
                  >
                    <Share2 className="w-5 h-5" />
                    Compartir credencial
                  </button>
                </div>
              </div>

              {/* Verification URL */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-indigo-600" />
                  URL de Verificación
                </h3>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={credential?.verification_url || ''}
                    className="flex-1 px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-600 truncate"
                  />
                  <button
                    onClick={copyVerificationUrl}
                    className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    {copied ? (
                      <Check className="w-5 h-5 text-green-600" />
                    ) : (
                      <Copy className="w-5 h-5 text-gray-600" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Cualquier persona puede verificar tu credencial escaneando el QR o visitando esta URL
                </p>
              </div>

              {/* Credential Details */}
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-indigo-600" />
                  Datos de la Credencial
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Nombre</span>
                    <span className="font-medium text-gray-900">{cred?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Email</span>
                    <span className="font-medium text-gray-900">{cred?.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Membresía</span>
                    <span className="font-medium text-gray-900">{cred?.membership_type || 'Sin membresía'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Miembro desde</span>
                    <span className="font-medium text-gray-900">
                      {cred?.member_since ? new Date(cred.member_since).toLocaleDateString('es-MX') : '-'}
                    </span>
                  </div>
                  {cred?.membership_expires && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Vigencia</span>
                      <span className="font-medium text-gray-900">
                        {new Date(cred.membership_expires).toLocaleDateString('es-MX')}
                      </span>
                    </div>
                  )}
                  {cred?.city && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Ubicación</span>
                      <span className="font-medium text-gray-900">{cred.city}, {cred.state}</span>
                    </div>
                  )}
                  {cred?.microsite_slug && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Micrositio</span>
                      <a 
                        href={`/asesor/${cred.microsite_slug}`}
                        target="_blank"
                        className="font-medium text-indigo-600 hover:underline flex items-center gap-1"
                      >
                        Ver perfil <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Specialties */}
              {cred?.specialties?.length > 0 && (
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">Especialidades</h3>
                  <div className="flex flex-wrap gap-2">
                    {cred.specialties.map((spec, i) => (
                      <span 
                        key={i}
                        className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm"
                      >
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Info Banner */}
          <div className="mt-8 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
            <div className="flex items-start gap-4">
              <Shield className="w-10 h-10 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold mb-2">¿Qué es la Credencial Digital UPIM?</h3>
                <p className="text-indigo-100 text-sm leading-relaxed">
                  Tu credencial digital certifica tu membresía activa como profesional inmobiliario 
                  afiliado a UPIM. Clientes, colegas y socios pueden verificar tu estatus escaneando 
                  el código QR o visitando la URL de verificación. Esta credencial demuestra tu 
                  compromiso con los estándares de profesionalismo y ética de la asociación.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
